import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
    Calendar, 
    Phone, 
    ChevronRight,
    MapPin,
    Clock,
    UtensilsCrossed,
    ArrowRight,
    Gift,
    Info,
    FileText
} from 'lucide-react';
import { useSettings } from '../components/ClientLayout';

// ============================================================
// ALDRIN RESTAURANT - PAGE D'ACCUEIL GASTRONOMIQUE
// Design: Bandeau blanc centré sur photo de fond
// ============================================================

// Assets
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_resto-stock-manager-3/artifacts/0l7b747s_9F91CBF8-DC1E-49E7-9C24-96206851AEA7.PNG';
const RESTAURANT_IMAGE = 'https://customer-assets.emergentagent.com/job_resto-stock-manager-3/artifacts/v6qrj8fz_9681739E-B83F-4602-AA25-E6E2EFE611F7.PNG';

// Couleur principale
const BRAND_GREEN = '#1a3d2c';

// Photos de plats
const DISH_PHOTOS = [
    {
        id: 1,
        name: 'Poulet braisé',
        description: 'Poulet mariné et grillé accompagné de nos garnitures maison',
        image: 'https://images.unsplash.com/photo-1772693471187-6e7d364f99ee?w=400&h=300&fit=crop'
    },
    {
        id: 2,
        name: 'Poisson grillé',
        description: 'Poisson frais grillé avec sauce maison',
        image: 'https://images.unsplash.com/photo-1677094544034-01421fc6c874?w=400&h=300&fit=crop'
    },
    {
        id: 3,
        name: 'Fruits de mer',
        description: 'Assortiment de fruits de mer frais du jour',
        image: 'https://images.unsplash.com/photo-1765100479761-2359e22fbe16?w=400&h=300&fit=crop'
    },
    {
        id: 4,
        name: 'Plat du jour',
        description: 'Une spécialité préparée chaque jour',
        image: 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400&h=300&fit=crop'
    }
];

const ClientHomePage = () => {
    const { settings } = useSettings();
    
    return (
        <div className="min-h-screen bg-neutral-100">
            
            {/* ============================================ */}
            {/* HERO SECTION - Photo de fond + Bandeau blanc */}
            {/* ============================================ */}
            <section className="relative min-h-screen">
                
                {/* ARRIÈRE-PLAN - Photo du restaurant */}
                <div className="absolute inset-0 hidden lg:block">
                    <img 
                        src={RESTAURANT_IMAGE}
                        alt="Aldrin Restaurant"
                        className="w-full h-full object-cover"
                    />
                    {/* Overlay sombre pour lisibilité */}
                    <div className="absolute inset-0 bg-black/40" />
                </div>
                
                {/* MOBILE - Photo en haut */}
                <div className="lg:hidden">
                    <div className="relative h-48 sm:h-64">
                        <img 
                            src={RESTAURANT_IMAGE}
                            alt="Aldrin Restaurant"
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/30" />
                    </div>
                </div>
                
                {/* BANDEAU CENTRAL BLANC */}
                <div className="relative lg:absolute lg:inset-0 lg:flex lg:items-center lg:justify-center lg:py-12">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                        className="w-full lg:w-[95%] lg:max-w-[1000px] bg-white lg:rounded-3xl lg:shadow-2xl"
                    >
                        <div className="px-6 sm:px-10 lg:px-16 py-10 lg:py-14">
                            
                            {/* Logo */}
                            <div className="flex justify-center mb-8">
                                <img 
                                    src={LOGO_URL} 
                                    alt="Aldrin Restaurant" 
                                    className="w-[55%] sm:w-[45%] lg:w-[40%] max-w-[350px] object-contain"
                                    data-testid="hero-logo"
                                />
                            </div>
                            
                            {/* Ligne décorative */}
                            <div
                                className="w-24 h-0.5 mx-auto mb-8"
                                style={{ backgroundColor: BRAND_GREEN }}
                            />
                            
                            {/* Titre */}
                            <h1
                                className="text-3xl sm:text-4xl lg:text-5xl font-light text-center mb-4 tracking-wide"
                                style={{ color: BRAND_GREEN }}
                            >
                                Bienvenue chez Aldrin
                            </h1>
                            
                            {/* Description */}
                            <p
                                className="text-base sm:text-lg text-gray-500 text-center mb-10 max-w-lg mx-auto leading-relaxed font-light"
                            >
                                {settings.welcome_message || "Une expérience culinaire unique alliant saveurs authentiques et ambiance chaleureuse."}
                            </p>
                            
                            {/* Bouton principal */}
                            <div className="flex justify-center mb-5">
                                <Link to="/client/menu" data-testid="view-menu-btn">
                                    <button 
                                        className="group flex items-center gap-3 py-4 px-10 rounded-full text-white font-medium tracking-wide transition-all duration-300 hover:scale-105 hover:shadow-xl"
                                        style={{ backgroundColor: BRAND_GREEN }}
                                    >
                                        <UtensilsCrossed className="w-5 h-5" />
                                        <span>Voir le menu</span>
                                        <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                    </button>
                                </Link>
                            </div>
                            
                            {/* Boutons secondaires */}
                            <div className="flex flex-wrap justify-center gap-3 mb-10 px-4">
                                <Link to="/client/reservation" data-testid="reservation-btn">
                                    <button 
                                        className="flex items-center gap-2 py-2.5 px-5 rounded-full border font-medium text-sm tracking-wide transition-all duration-300 hover:scale-105"
                                        style={{ borderColor: BRAND_GREEN, color: BRAND_GREEN }}
                                    >
                                        <Calendar className="w-4 h-4" />
                                        Réservation
                                    </button>
                                </Link>
                                
                                <Link to="/client/contact" data-testid="contact-btn">
                                    <button 
                                        className="flex items-center gap-2 py-2.5 px-5 rounded-full border font-medium text-sm tracking-wide transition-all duration-300 hover:scale-105"
                                        style={{ borderColor: BRAND_GREEN, color: BRAND_GREEN }}
                                    >
                                        <Phone className="w-4 h-4" />
                                        Contact
                                    </button>
                                </Link>
                                
                                {/* Bouton Programme Fidélité */}
                                <Link to="/client/fidelite" data-testid="loyalty-btn">
                                    <button 
                                        className="flex items-center gap-2 py-2.5 px-5 rounded-full font-medium text-sm tracking-wide transition-all duration-300 hover:scale-105 text-white shadow-md"
                                        style={{ backgroundColor: '#166534' }}
                                    >
                                        <Gift className="w-4 h-4" />
                                        Fidélité
                                    </button>
                                </Link>
                            </div>
                            
                            {/* Ligne de séparation */}
                            <div className="border-t border-gray-100 pt-8">
                                
                                {/* Infos restaurant */}
                                <div className="flex flex-col sm:flex-row justify-center items-center gap-4 sm:gap-8 text-gray-400 text-sm">
                                    <div className="flex items-center gap-2">
                                        <Clock className="w-4 h-4" style={{ color: BRAND_GREEN }} />
                                        <span>08h00 – 19h00</span>
                                    </div>
                                    <div className="hidden sm:block w-1 h-1 rounded-full bg-gray-300" />
                                    <div className="flex items-center gap-2">
                                        <MapPin className="w-4 h-4" style={{ color: BRAND_GREEN }} />
                                        <span>Libreville, Gabon</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>
            
            {/* ============================================ */}
            {/* SECTION NOS SPÉCIALITÉS */}
            {/* ============================================ */}
            <section className="py-14 lg:py-20 bg-white">
                <div className="max-w-6xl mx-auto px-6">
                    
                    {/* Titre de section */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-10"
                    >
                        <h2 
                            className="text-3xl lg:text-4xl font-light tracking-wide mb-4"
                            style={{ color: BRAND_GREEN }}
                        >
                            Nos spécialités
                        </h2>
                        <div 
                            className="w-16 h-0.5 mx-auto"
                            style={{ backgroundColor: BRAND_GREEN }}
                        />
                    </motion.div>
                    
                    {/* Grille de plats */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {DISH_PHOTOS.map((dish, index) => (
                            <motion.div
                                key={dish.id}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                data-testid={`dish-card-${dish.id}`}
                            >
                                <Link to="/client/menu">
                                    <div className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-1">
                                        {/* Image */}
                                        <div className="aspect-[4/3] overflow-hidden">
                                            <img 
                                                src={dish.image} 
                                                alt={dish.name}
                                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                            />
                                        </div>
                                        {/* Contenu */}
                                        <div className="p-5">
                                            <h3 
                                                className="font-medium text-lg mb-2"
                                                style={{ color: BRAND_GREEN }}
                                            >
                                                {dish.name}
                                            </h3>
                                            <p className="text-gray-400 text-sm leading-relaxed">
                                                {dish.description}
                                            </p>
                                        </div>
                                    </div>
                                </Link>
                            </motion.div>
                        ))}
                    </div>
                    
                    {/* Lien vers le menu */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        className="mt-8 text-center"
                    >
                        <Link 
                            to="/client/menu" 
                            className="inline-flex items-center gap-2 font-medium tracking-wide hover:underline"
                            style={{ color: BRAND_GREEN }}
                        >
                            Découvrir toute la carte
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                    </motion.div>
                </div>
            </section>
            
            {/* ============================================ */}
            {/* SECTION NOUS TROUVER - Premium */}
            {/* ============================================ */}
            <section className="py-14 lg:py-20 bg-neutral-50">
                <div className="max-w-4xl mx-auto px-6">
                    
                    {/* Titre */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-6"
                    >
                        <h2 
                            className="text-3xl lg:text-4xl font-light tracking-wide mb-4"
                            style={{ color: BRAND_GREEN }}
                        >
                            Nous trouver
                        </h2>
                        <div 
                            className="w-16 h-0.5 mx-auto mb-6"
                            style={{ backgroundColor: BRAND_GREEN }}
                        />
                        
                        {/* Adresse détaillée */}
                        <p className="text-gray-500 text-base lg:text-lg leading-relaxed max-w-xl mx-auto">
                            Damas, Libreville, au 4ème carrefour, après Prix Import d'Awendjé, avant de monter à l'école Sacré-Cœur.
                        </p>
                    </motion.div>
                    
                    {/* Carte Google Maps - Style Premium */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="relative mb-8"
                    >
                        {/* Conteneur avec effet de profondeur */}
                        <div className="relative">
                            {/* Ombre externe élégante */}
                            <div 
                                className="absolute -inset-1 rounded-3xl opacity-20 blur-xl"
                                style={{ backgroundColor: BRAND_GREEN }}
                            />
                            {/* Carte */}
                            <div className="relative bg-white rounded-2xl overflow-hidden shadow-xl ring-1 ring-black/5">
                                <iframe
                                    src="https://www.google.com/maps?q=9FQH%2BHH+Libreville,+Gabon&z=17&output=embed"
                                    width="100%"
                                    height="100%"
                                    style={{ border: 0 }}
                                    allowFullScreen=""
                                    loading="lazy"
                                    title="Localisation Aldrin Restaurant"
                                    className="w-full h-[250px] sm:h-[300px] lg:h-[350px]"
                                />
                            </div>
                        </div>
                    </motion.div>
                    
                    {/* Informations pratiques */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        className="flex flex-wrap justify-center gap-6 lg:gap-10 text-gray-500 text-sm mb-8"
                    >
                        <div className="flex items-center gap-2">
                            <div 
                                className="p-2 rounded-full"
                                style={{ backgroundColor: `${BRAND_GREEN}10` }}
                            >
                                <MapPin className="w-4 h-4" style={{ color: BRAND_GREEN }} />
                            </div>
                            <span>Libreville, Gabon</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div 
                                className="p-2 rounded-full"
                                style={{ backgroundColor: `${BRAND_GREEN}10` }}
                            >
                                <Phone className="w-4 h-4" style={{ color: BRAND_GREEN }} />
                            </div>
                            <span>{settings.contactInfo?.phone || "+241 77 71 27 78"}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div 
                                className="p-2 rounded-full"
                                style={{ backgroundColor: `${BRAND_GREEN}10` }}
                            >
                                <Clock className="w-4 h-4" style={{ color: BRAND_GREEN }} />
                            </div>
                            <span>08h00 – 19h00</span>
                        </div>
                    </motion.div>
                    
                    {/* Bouton Google Maps */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        className="text-center"
                    >
                        <a 
                            href="https://www.google.com/maps/search/?api=1&query=9FQH%2BHH+Libreville+Gabon"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 py-3 px-6 rounded-full font-medium text-sm tracking-wide transition-all duration-300 hover:scale-105 hover:shadow-lg"
                            style={{ backgroundColor: BRAND_GREEN, color: 'white' }}
                        >
                            <MapPin className="w-4 h-4" />
                            Ouvrir dans Google Maps
                            <ArrowRight className="w-4 h-4" />
                        </a>
                    </motion.div>
                </div>
            </section>
            
            {/* ============================================ */}
            {/* FOOTER */}
            {/* ============================================ */}
            <footer 
                className="py-10 px-6"
                style={{ backgroundColor: BRAND_GREEN }}
            >
                <div className="max-w-4xl mx-auto">
                    {/* Logo */}
                    <div className="text-center mb-8">
                        <img 
                            src={LOGO_URL} 
                            alt="Aldrin Restaurant" 
                            className="h-14 mx-auto mb-6 opacity-90"
                        />
                    </div>
                    
                    {/* Navigation links */}
                    <div className="flex flex-wrap justify-center gap-6 mb-8">
                        <Link 
                            to="/client/menu" 
                            className="text-white/70 hover:text-white text-sm transition-colors"
                        >
                            Notre Carte
                        </Link>
                        <Link 
                            to="/client/reservation" 
                            className="text-white/70 hover:text-white text-sm transition-colors"
                        >
                            Réservation
                        </Link>
                        <Link 
                            to="/client/about" 
                            className="text-white/70 hover:text-white text-sm transition-colors"
                        >
                            À propos
                        </Link>
                        <Link 
                            to="/client/terms" 
                            className="text-white/70 hover:text-white text-sm transition-colors"
                        >
                            Conditions générales
                        </Link>
                        <Link 
                            to="/client/contact" 
                            className="text-white/70 hover:text-white text-sm transition-colors"
                        >
                            Contact
                        </Link>
                    </div>
                    
                    {/* Info */}
                    <div className="flex flex-wrap justify-center gap-6 text-white/60 text-sm mb-6">
                        <span>Libreville, Gabon</span>
                        <span>•</span>
                        <span>08h00 – 19h00</span>
                        <span>•</span>
                        <span>+241 77 71 27 78</span>
                    </div>
                    
                    <p className="text-white/40 text-xs text-center">
                        © {new Date().getFullYear()} Aldrin Restaurant. Tous droits réservés.
                    </p>
                </div>
            </footer>
        </div>
    );
};

export default ClientHomePage;
